import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Loader2, Eye, EyeOff, ChevronRight, RotateCw } from "lucide-react";

import { isConnectionError } from "@/lib/api";
import LoginShell from "@/components/brand/LoginShell";
import { SHOW_PREVIEW_BADGE } from "@/lib/environment";
import { memberLoginErrorKey, isLoginTimeout } from "@/lib/memberLoginError";
import { loginTarget } from "@/lib/returnPath";
import { useI18n } from "@/i18n/I18nContext";
import { useMemberAuth } from "../MemberAuthContext";
import { LoginAccessibilityMenu } from "../components/LoginAccessibilityMenu";
import { LoginA11yHint } from "../components/LoginA11yHint";
import { EnvBlocker, CoreGateNotice, InternalAccessNote } from "../components/EnvGate";
import { useCoreCheck } from "../useCoreCheck";

// M01 = welcome entry (shared shell copy), M29 = sign-in in detail (same form, own heading and a way back).
const VARIANTS = {
  welcome: { m: "M01", testid: "member-login-page" },
  detail: { m: "M29", testid: "member-login-detail", title: "Anmelden", subtitle: "Melde dich mit deinem persönlichen Mitgliederkonto an." },
};

// Why the previous session in this tab ended. Unsent texts are never kept, so the notice says so honestly.
function SessionNotice({ ended }) {
  if (ended === "expired") return (
    <div role="alert" data-testid="member-session-expired" className="vl-login__notice">
      <p className="font-semibold">Sitzung abgelaufen – bitte erneut anmelden.</p>
      <p>Nicht gesendete Eingaben wurden aus Sicherheitsgründen verworfen. Sie werden weder gespeichert noch in ein anderes Konto übernommen.</p>
    </div>
  );
  if (ended === "logout") return <p role="status" data-testid="member-logged-out" className="vl-login__notice">Du bist abgemeldet.</p>;
  return null;
}

export default function MemberLogin({ variant = "welcome" }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useI18n();
  const { member, login, ended } = useMemberAuth();
  const gate = useCoreCheck();
  const v = VARIANTS[variant] || VARIANTS.welcome;
  // Validated internal target only; the target page checks the signed-in account's rights itself.
  const target = loginTarget(ended, location.state);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [errorKey, setErrorKey] = useState("");
  const [retryable, setRetryable] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (member) navigate(target, { replace: true }); }, [member, navigate, target]);

  const submit = async (e) => {
    e?.preventDefault();
    if (gate.status !== "ok") return;
    setErrorKey(""); setRetryable(false); setLoading(true);
    try {
      // null: the AuthContext discarded a stale answer; stay on this page without an error.
      const data = await login(email, password);
      if (data) navigate(target, { replace: true });
    } catch (err) {
      if (isLoginTimeout(err)) setErrorKey("member.login.timeout");
      else if (isConnectionError(err)) { setErrorKey("common.connection_error"); setRetryable(true); }
      else setErrorKey(memberLoginErrorKey(err));
    } finally { setLoading(false); }
  };

  return (
    <LoginShell variant="member" title={v.title} subtitle={v.subtitle} testId={v.testid}
      accessibility={<LoginAccessibilityMenu />} notice={<><LoginA11yHint /><SessionNotice ended={ended} /></>}
      afterForm={<>
        <InternalAccessNote gate={gate} />
        {SHOW_PREVIEW_BADGE && (
          <p data-testid="member-login-preview-badge">{t("member.login.preview_badge")}</p>
        )}
      </>}>
      {gate.status === "blocked" ? <EnvBlocker problems={gate.problems} /> : (
        <form onSubmit={submit} data-testid="member-login-form" data-m={v.m}>
          <div>
            <label htmlFor="m-email">{t("member.login.email")}</label>
            <input id="m-email" type="email" autoComplete="username" required value={email} placeholder="Deine E-Mail-Adresse"
              onChange={(e) => setEmail(e.target.value)} data-testid="member-email-input" />
          </div>
          <div>
            <label htmlFor="m-pw">{t("member.login.password")}</label>
            <div className="vl-login__password">
              <input id="m-pw" type={showPw ? "text" : "password"} autoComplete="current-password" required value={password} placeholder="Dein Passwort"
                onChange={(e) => setPassword(e.target.value)} data-testid="member-password-input" />
              {/* One signal only: constant name, state via aria-pressed. */}
              <button type="button" onClick={() => setShowPw((s) => !s)} data-testid="member-toggle-password" aria-controls="m-pw" aria-pressed={showPw}>
                {showPw ? <EyeOff size={20} aria-hidden="true" /> : <Eye size={20} aria-hidden="true" />}
                <span className="sr-only">{t("member.login.show_password")}</span>
              </button>
            </div>
          </div>
          <Link to="/passwort-vergessen" data-testid="member-login-forgot" className="vl-login__forgot">Passwort vergessen?</Link>
          {errorKey && (
            <div role="alert" data-testid="member-login-error" className="vl-login__error">
              <p>{t(errorKey)}</p>
              {retryable && <button type="button" onClick={() => submit()} data-testid="member-login-retry"><RotateCw size={16} aria-hidden="true" /> {t("common.retry")}</button>}
            </div>
          )}
          <CoreGateNotice gate={gate} />
          <button type="submit" disabled={loading || gate.status !== "ok"} data-testid="member-login-submit">
            {loading ? <Loader2 className="animate-spin" size={20} aria-label="Anmeldung läuft" /> : <>{t("member.login.submit")}<ChevronRight size={20} aria-hidden="true" /></>}
          </button>
          {variant === "detail"
            ? <p className="vl-login__register"><Link to="/login" data-testid="member-login-back">Zurück zur Anmeldung</Link></p>
            : <p className="vl-login__register">Neu hier? <Link to="/registrieren" data-testid="member-login-register">Konto erstellen</Link></p>}
          <p className="vl-login__purpose">{t("member.login.no_dating")}</p>
        </form>
      )}
    </LoginShell>
  );
}
